import React, { useState, useEffect, useCallback } from "react";
import { ChatSession } from "../types";
import { apiService } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import {
  Plus,
  MessageSquare,
  Trash2,
  Calendar,
  Hash,
  PanelLeftClose,
  PanelLeftOpen,
  Edit3,
  Check,
  X,
} from "lucide-react";
import DeleteConfirmModal from "./DeleteConfirmModal";

interface SessionManagerProps {
  currentSessionId: string;
  sessionTitles: { [sessionId: string]: string };
  onSessionChange: (sessionId: string) => void;
  onNewSession: () => void;
  // onClearSession: (sessionId: string) => void; // COMMENTED OUT - not needed
  onDeleteSession: (sessionId: string) => void;
  isCollapsed?: boolean;
  toggleSidebar: () => void;
  refreshTrigger?: number; // Add refresh trigger prop
  newSession?: ChatSession; // Add new session prop
}

const SessionManager: React.FC<SessionManagerProps> = ({
  currentSessionId,
  sessionTitles,
  onSessionChange,
  onNewSession,
  // onClearSession, // COMMENTED OUT - not needed
  onDeleteSession,
  isCollapsed = false,
  toggleSidebar,
  refreshTrigger,
  newSession,
}) => {
  const { user } = useAuth(); // Get current user for thread ID generation
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState<string>("");
  const [isRenaming, setIsRenaming] = useState(false);

  // Helper to correctly parse server timestamps (UTC without timezone info)
  const parseServerDate = (isoString?: string | null): Date => {
    if (!isoString) return new Date();

    // If the string already includes timezone information, use it directly
    if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(isoString)) {
      return new Date(isoString);
    }

    // Backend sends naive UTC (e.g. "2025-12-02T10:00:00"),
    // so append "Z" to interpret it as UTC in the browser
    return new Date(`${isoString}Z`);
  };

  // Load sessions from localStorage and sync with database
  const loadSessions = useCallback(async () => {
    // Only fetch from backend if user is authenticated
    if (!user) {
      // If user is not authenticated, clear any existing sessions and return
      setSessions([]);
      localStorage.removeItem("chat_sessions");
      localStorage.removeItem("current_session_id");
      return;
    }

    try {
      // First, try to get active threads from the backend
      const activeThreads = await apiService.getActiveThreads();

      // Convert backend threads to ChatSession format, filtering out empty sessions
      // but keep the current session even if it has no messages yet
      const dbSessions: ChatSession[] = activeThreads.threads
        .filter(
          (thread) =>
            thread.message_count > 0 || thread.thread_id === currentSessionId
        ) // Show sessions with messages or current session
        .map((thread) => ({
          id: thread.thread_id,
          name: sessionTitles[thread.thread_id] || thread.name,
          created_at: parseServerDate(thread.last_message_time),
          last_message_at: parseServerDate(thread.last_message_time),
          message_count: thread.message_count,
        }));

      // Only use database sessions - but preserve the current session if it's new and empty
      let mergedSessions = [...dbSessions];

      // If current session is not in database sessions, add it as a new empty session
      if (
        currentSessionId &&
        !dbSessions.some((session) => session.id === currentSessionId)
      ) {
        const newEmptySession: ChatSession = {
          id: currentSessionId,
          name: sessionTitles[currentSessionId] || "New Chat",
          created_at: new Date(),
          last_message_at: new Date(),
          message_count: 0,
        };
        mergedSessions = [newEmptySession, ...mergedSessions];
      }

      // Don't clear localStorage immediately - only clear it if we're not preserving a new session
      const savedSessions = localStorage.getItem("chat_sessions");
      if (savedSessions && !currentSessionId) {
        console.log(
          "Clearing legacy localStorage sessions for user-specific filtering"
        );
        localStorage.removeItem("chat_sessions");
      }

      // No need to create default session - only show sessions with messages

      // Sort by last message time (most recent first)
      mergedSessions.sort(
        (a, b) => b.last_message_at.getTime() - a.last_message_at.getTime()
      );

      setSessions(mergedSessions);

      // Update localStorage with merged sessions
      localStorage.setItem("chat_sessions", JSON.stringify(mergedSessions));

      // Try to restore saved session if no current session is selected
      if (!currentSessionId) {
        const savedSessionId = localStorage.getItem("current_session_id");
        if (savedSessionId) {
          const sessionExists = mergedSessions.some(
            (session) => session.id === savedSessionId
          );
          if (sessionExists) {
            onSessionChange(savedSessionId);
          } else {
            // Session no longer exists, clear it
            localStorage.removeItem("current_session_id");
          }
        }
      }
    } catch (error) {
      console.error("Error loading sessions from database:", error);

      // Fallback to localStorage only
      const savedSessions = localStorage.getItem("chat_sessions");
      let parsedSessions: ChatSession[] = [];

      if (savedSessions) {
        try {
          parsedSessions = JSON.parse(savedSessions).map((session: any) => ({
            ...session,
            created_at: new Date(session.created_at),
            last_message_at: new Date(session.last_message_at),
          }));
        } catch (error) {
          console.error("Error loading sessions:", error);
          parsedSessions = [];
        }
      }

      // Filter out empty sessions from localStorage as well, but keep current session
      parsedSessions = parsedSessions.filter(
        (session) =>
          session.message_count > 0 || session.id === currentSessionId
      );

      setSessions(parsedSessions);

      // Try to restore saved session in fallback case too
      if (!currentSessionId) {
        const savedSessionId = localStorage.getItem("current_session_id");
        if (savedSessionId) {
          const sessionExists = parsedSessions.some(
            (session) => session.id === savedSessionId
          );
          if (sessionExists) {
            onSessionChange(savedSessionId);
          } else {
            // Session no longer exists, clear it
            localStorage.removeItem("current_session_id");
          }
        }
      }
    }
  }, [user, currentSessionId, sessionTitles, onSessionChange]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Refresh sessions when refreshTrigger changes (new session created)
  useEffect(() => {
    if (refreshTrigger) {
      loadSessions();
    }
  }, [refreshTrigger, loadSessions]);

  // Add new session to the list when provided
  useEffect(() => {
    if (newSession) {
      setSessions((prev) => {
        // Check if session already exists
        const exists = prev.some((session) => session.id === newSession.id);
        if (!exists) {
          const updatedSessions = [newSession, ...prev];
          // Also save to localStorage immediately
          localStorage.setItem(
            "chat_sessions",
            JSON.stringify(updatedSessions)
          );
          return updatedSessions;
        }
        return prev;
      });
    }
  }, [newSession]);

  // Update session activity when messages are sent/received
  const updateSessionActivity = useCallback((sessionId: string) => {
    setSessions((prevSessions) =>
      prevSessions.map((session) =>
        session.id === sessionId
          ? {
              ...session,
              last_message_at: new Date(),
              message_count: session.message_count + 1,
            }
          : session
      )
    );
  }, []);

  // Expose updateSessionActivity globally for ChatInterface to use
  useEffect(() => {
    (window as any).updateSessionActivity = updateSessionActivity;
    return () => {
      delete (window as any).updateSessionActivity;
    };
  }, [updateSessionActivity]);

  // Create new session
  const handleNewSession = () => {
    // Generate a unique thread ID with user context
    const timestamp = Date.now();
    const userPrefix = user?.id ? user.id.slice(-8) : "anon";
    const newSessionId = `thread_${userPrefix}_${timestamp}`;

    const newSession: ChatSession = {
      id: newSessionId,
      name: "New Chat", // Placeholder name - will be updated when first message is sent
      created_at: new Date(),
      last_message_at: new Date(),
      message_count: 0,
    };

    // Add the new session to the current sessions list immediately
    setSessions((prevSessions) => [newSession, ...prevSessions]);

    // Don't save to localStorage anymore since we're using MongoDB
    // Just trigger the callbacks
    onNewSession();
    onSessionChange(newSession.id);
  };

  // Clear session - COMMENTED OUT (not needed)
  // const handleClearSession = async (sessionId: string) => {
  //   if (window.confirm("Are you sure you want to clear this conversation?")) {
  //     try {
  //       await onClearSession(sessionId);

  //       // Update session in local storage
  //       const updatedSessions = sessions.map((session) =>
  //         session.id === sessionId
  //           ? { ...session, message_count: 0, last_message_at: new Date() }
  //           : session
  //       );
  //       saveSessions(updatedSessions);
  //     } catch (error) {
  //       console.error("Error clearing session:", error);
  //     }
  //   }
  // };

  // Show delete confirmation modal
  const showDeleteConfirmation = (sessionId: string) => {
    setSessionToDelete(sessionId);
    setDeleteModalOpen(true);
  };

  // Delete session completely
  const handleDeleteSession = async () => {
    if (!sessionToDelete) return;

    try {
      await onDeleteSession(sessionToDelete);

      // Remove session from local state
      setSessions((prevSessions) =>
        prevSessions.filter((session) => session.id !== sessionToDelete)
      );

      // If the deleted session was the current one, clear the current session
      if (sessionToDelete === currentSessionId) {
        onSessionChange(""); // This will now be handled gracefully
      }
    } catch (error) {
      console.error("Error deleting session:", error);
    }
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setSessionToDelete(null);
  };

  // Start editing session title
  const startRenaming = (sessionId: string, currentTitle: string) => {
    setEditingSessionId(sessionId);
    setEditingTitle(currentTitle);
  };

  // Cancel renaming
  const cancelRenaming = () => {
    setEditingSessionId(null);
    setEditingTitle("");
  };

  // Save renamed title
  const saveRename = async () => {
    if (!editingSessionId || !editingTitle.trim()) return;

    setIsRenaming(true);
    try {
      await apiService.renameConversation(
        editingSessionId,
        editingTitle.trim()
      );

      // Update local sessions
      setSessions((prevSessions) =>
        prevSessions.map((session) =>
          session.id === editingSessionId
            ? { ...session, name: editingTitle.trim() }
            : session
        )
      );

      // Clear editing state
      setEditingSessionId(null);
      setEditingTitle("");

      // Refresh sessions to get updated data from backend
      await loadSessions();
    } catch (error) {
      console.error("Error renaming conversation:", error);
      // Could add a toast notification here for error feedback
    } finally {
      setIsRenaming(false);
    }
  };

  // Handle Enter key in rename input
  const handleRenameKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      saveRename();
    } else if (e.key === "Escape") {
      cancelRenaming();
    }
  };

  // Format date for display
  const formatDate = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Expose updateSessionActivity to parent
  React.useEffect(() => {
    (window as any).updateSessionActivity = updateSessionActivity;
  }, [updateSessionActivity]);

  if (isCollapsed) {
    return (
      <div className="w-16 flex-shrink-0 bg-white dark:bg-neutral-800 border-r border-gray-200 dark:border-neutral-700 flex flex-col items-center h-full">
        <div className="pt-4">
          <button
            onClick={toggleSidebar}
            className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 group"
            title="Show conversations"
          >
            <PanelLeftOpen
              size={20}
              className="group-hover:scale-110 transition-transform"
            />
          </button>
          <div className="mt-4 text-xs text-gray-500 dark:text-neutral-400 text-center">
            <div className="font-medium">Chats</div>
            <div className="text-gray-400 dark:text-neutral-500">
              {sessions.length}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white dark:bg-neutral-800 border-r border-gray-200 dark:border-neutral-700 w-full flex-shrink-0">
      {/* Header */}
      <div className="p-3 border-b border-gray-200 dark:border-neutral-700">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-800 dark:text-neutral-200 flex items-center">
            <MessageSquare className="w-4 h-4 mr-2" />
            Conversations
          </h2>
          <button
            onClick={toggleSidebar}
            className="p-2 text-gray-500 dark:text-neutral-400 hover:text-gray-700 dark:hover:text-neutral-200 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-lg transition-all duration-200 group"
            title="Hide conversations"
          >
            <PanelLeftClose className="w-4 h-4 group-hover:scale-110 transition-transform" />
          </button>
        </div>

        <button
          onClick={handleNewSession}
          className="w-full mt-2 px-3 py-2 bg-orange-500 dark:bg-orange-600 text-white rounded-lg hover:bg-orange-600 dark:hover:bg-orange-700 transition-colors flex items-center justify-center text-sm"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Chat
        </button>
      </div>

      {/* Sessions List */}
      <div
        className="flex-1 overflow-y-auto custom-scrollbar"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "#d1d5db transparent",
        }}
      >
        {sessions.length === 0 ? (
          <div className="p-3 text-center text-gray-500 dark:text-neutral-400">
            <MessageSquare className="w-10 h-10 mx-auto mb-2 text-gray-300 dark:text-neutral-600" />
            <p className="text-sm">No conversations yet</p>
            <p className="text-xs text-gray-400 dark:text-neutral-500 mt-1">
              Send a message to start your first conversation
            </p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {sessions.map((session) => (
              <div
                key={session.id}
                className={`group relative p-2 rounded-lg transition-colors ${
                  editingSessionId === session.id
                    ? "bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800/30"
                    : `cursor-pointer ${
                        session.id === currentSessionId &&
                        currentSessionId !== ""
                          ? "bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800/30"
                          : "hover:bg-gray-50 dark:hover:bg-neutral-700/50"
                      }`
                }`}
                onClick={() =>
                  editingSessionId !== session.id && onSessionChange(session.id)
                }
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    {editingSessionId === session.id ? (
                      // Rename input - constrain input width to leave room for buttons
                      <div
                        className="flex items-center gap-1.5 w-full min-w-0"
                        // Prevent clicks inside the rename row from triggering the parent session click
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="text"
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          onKeyDown={handleRenameKeyPress}
                          className="min-w-0 px-2 py-1 text-sm bg-white dark:bg-neutral-700 border border-gray-300 dark:border-neutral-600 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          placeholder="Title..."
                          autoFocus
                          disabled={isRenaming}
                        />
                        <div className="flex gap-1 flex-shrink-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              saveRename();
                            }}
                            disabled={isRenaming || !editingTitle.trim()}
                            className="p-1 text-green-500 hover:text-green-600 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                            title="Save"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              cancelRenaming();
                            }}
                            disabled={isRenaming}
                            className="p-1 text-gray-500 hover:text-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Normal display
                      <>
                        <h3 className="font-medium text-gray-800 dark:text-neutral-200 truncate text-sm">
                          {sessionTitles[session.id] || session.name}
                        </h3>
                        <div className="flex items-center mt-1 text-xs text-gray-500 dark:text-neutral-400 space-x-2">
                          <div className="flex items-center">
                            <Hash className="w-3 h-3 mr-1" />
                            {session.message_count}
                          </div>
                          <div className="flex items-center">
                            <Calendar className="w-3 h-3 mr-1" />
                            {formatDate(session.last_message_at)}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {editingSessionId !== session.id && (
                    <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-all">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startRenaming(
                            session.id,
                            sessionTitles[session.id] || session.name
                          );
                        }}
                        className="p-1 text-gray-400 dark:text-neutral-500 hover:text-blue-500 dark:hover:text-blue-400 transition-all"
                        title="Rename conversation"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          showDeleteConfirmation(session.id);
                        }}
                        className="p-1 text-gray-400 dark:text-neutral-500 hover:text-red-500 dark:hover:text-red-400 transition-all"
                        title="Delete conversation permanently"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteSession}
        title="Delete Conversation"
        message="Are you sure you want to permanently delete this conversation? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default SessionManager;
